import { Contract } from '../../../../../types/contract'
import React, { useCallback, useEffect, useState } from 'react'
import { PaymentMethodType } from '../../../../../enums/payment-method-type'
import { centsClient } from '../../../../../api/cents/clients/cents-client'
import { DialogError } from '../../../common/dialogs/DialogError'
import { translations } from '../../../../../utils/translations/translations-manager'
import ActivityProgression from '../../../common/ActivityProgression'
import { PaymentMethodCollection } from '../../../payment/payment-method/collection/PaymentMethodCollection'
import { ContractPaymentInfoBanner } from '../../subscriptions/ContractPaymentBanner'
import { Intent } from './ContractActivationIntent'
import { Finalization } from './ContractActivationFinalization'

export function ContractActivationPayment({ contract, success }: { contract: Contract; success: () => void }): JSX.Element {
  const [state, setState] = useState<'payment_method_save' | 'intent' | 'payment' | 'finalization' | 'success'>('payment_method_save')
  const [error, setError] = useState<unknown | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [setupIntentId, setSetupIntentId] = useState<string>()
  const onSuccessfulIntent = useCallback(async (clientSecret: string) => {
    setClientSecret(clientSecret)
    setState('payment')
  }, [])

  const onFailedIntent = useCallback((error: unknown) => {
    setError(error)
  }, [])

  const onSuccessfulCollection = useCallback((setupIntentId: string) => {
    setSetupIntentId(setupIntentId)
    setState('finalization')
  }, [])

  const onFailedCollection = useCallback((error: unknown) => {
    setError(error)
  }, [])

  const onSuccessfulFinalization = useCallback(async () => {
    setState('success')
    success()
  }, [success])

  const onFailedFinalization = useCallback((error: unknown) => {
    setError(error)
  }, [])

  const ensurePaymentMethodSave = useCallback(async () => {
    if (contract.paymentMethodType !== PaymentMethodType.stripeSetupPaymentSheet) {
      if (contract.charityId === undefined || contract._id === undefined) {
        setError(new Error('Contract ID and Charity ID must be defined'))
        return
      }
      try {
        await centsClient.contracts.patchContract(contract.charityId, contract._id, { paymentMethodType: PaymentMethodType.stripeSetupPaymentSheet })
      } catch (e) {
        setError(e)
      }
    }
    setState('intent')
  }, [])

  useEffect(() => {
    if (state === 'payment_method_save') {
      void ensurePaymentMethodSave()
    }
  }, [state])

  if (error) {
    return (
      <DialogError
        message={translations.texts.subscription_activation_error.toString()}
        onRetry={() => {
          setState('intent')
          setError(null)
        }}
        retryTitle={translations.texts.retry.toString()}
      />
    )
  }

  if (contract._id && contract.charityId) {
    let content
    if (state === 'payment_method_save') {
      content = <ActivityProgression status={{ status: 'pending' }} />
    } else if (state === 'intent') {
      content = <Intent success={onSuccessfulIntent} failure={onFailedIntent} charityId={contract.charityId} contractId={contract._id} />
    } else if (state === 'payment' && clientSecret) {
      content = <PaymentMethodCollection success={onSuccessfulCollection} failure={onFailedCollection} clientSecret={clientSecret} />
    } else if (state === 'finalization' && setupIntentId) {
      content = <Finalization charityId={contract.charityId} contractId={contract._id} success={onSuccessfulFinalization} failure={onFailedFinalization} />
    }

    return (
      <>
        <ContractPaymentInfoBanner contract={contract} className="mb-8" />
        {content}
      </>
    )
  }

  return <></> // TODO: Handle error, this should never happen
}
